// Smart Daara - Teacher Management Page
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { userAPI, courseAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription
} from '../../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../../components/ui/dropdown-menu';
import { 
  UserCog, Plus, Search, MoreVertical, Trash2, Pencil,
  Loader2, Mail, Phone, BookOpen, UserCheck, UserX, Link2, KeyRound, FileSpreadsheet
} from 'lucide-react';
import { toast } from 'sonner';
import PasswordResetDialog from '../../components/shared/PasswordResetDialog';
import StudentImportDialog from '../../components/shared/StudentImportDialog';

export default function TeacherManagement() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [teachers, setTeachers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [assignCoursesModalOpen, setAssignCoursesModalOpen] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    password: '',
    specialization: ''
  });

  // Course assignment state
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [teachersRes, coursesRes] = await Promise.all([
        userAPI.listTeachers({ tenant_id: user?.tenant_id }),
        courseAPI.listCourses({ tenant_id: user?.tenant_id })
      ]);
      setTeachers(teachersRes.data?.items || teachersRes.data || []);
      setCourses(coursesRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Loading error');
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      email: '',
      first_name: '',
      last_name: '',
      phone: '',
      password: '',
      specialization: ''
    });
  };

  const handleCreateTeacher = async () => {
    if (!formData.email || !formData.first_name || !formData.last_name || !formData.password) {
      toast.error(language === 'fr' ? 'Veuillez remplir tous les champs requis' : 'Please fill all required fields');
      return;
    }

    setSaving(true);
    try {
      await userAPI.createTeacher({
        ...formData,
        tenant_id: user?.tenant_id,
        role: 'teacher'
      });
      toast.success(language === 'fr' ? 'Enseignant créé avec succès!' : 'Teacher created successfully!');
      setCreateModalOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error creating teacher:', error);
      const message = error.response?.data?.detail || (language === 'fr' ? 'Erreur de création' : 'Creation error');
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleEditTeacher = async () => {
    if (!formData.email || !formData.first_name || !formData.last_name) {
      toast.error(language === 'fr' ? 'Veuillez remplir tous les champs requis' : 'Please fill all required fields');
      return;
    }

    setSaving(true);
    try {
      await userAPI.updateTeacher(selectedTeacher.id, {
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        specialization: formData.specialization
      });
      toast.success(language === 'fr' ? 'Enseignant mis à jour!' : 'Teacher updated!');
      setEditModalOpen(false);
      setSelectedTeacher(null);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error updating teacher:', error);
      toast.error(language === 'fr' ? 'Erreur de mise à jour' : 'Update error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTeacher = async () => {
    if (!selectedTeacher) return;

    setSaving(true);
    try {
      await userAPI.deleteTeacher(selectedTeacher.id);
      toast.success(language === 'fr' ? 'Enseignant supprimé!' : 'Teacher deleted!');
      setDeleteModalOpen(false);
      setSelectedTeacher(null);
      loadData();
    } catch (error) {
      console.error('Error deleting teacher:', error);
      toast.error(language === 'fr' ? 'Erreur de suppression' : 'Delete error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (teacher) => {
    try {
      await userAPI.updateTeacher(teacher.id, { is_active: !teacher.is_active });
      toast.success(language === 'fr' 
        ? (teacher.is_active ? 'Enseignant désactivé' : 'Enseignant activé')
        : (teacher.is_active ? 'Teacher deactivated' : 'Teacher activated')
      );
      loadData();
    } catch (error) {
      console.error('Error toggling teacher:', error);
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  const openEditModal = (teacher) => {
    setSelectedTeacher(teacher);
    setFormData({
      email: teacher.email || '',
      first_name: teacher.first_name || '',
      last_name: teacher.last_name || '',
      phone: teacher.phone || '',
      password: '',
      specialization: teacher.specialization || ''
    });
    setEditModalOpen(true);
  };

  const openAssignCoursesModal = (teacher) => {
    setSelectedTeacher(teacher);
    // Pre-select courses where this teacher is assigned
    const assignedIds = courses
      .filter(c => c.teacher_ids?.includes(teacher.id) || c.teacher_id === teacher.id)
      .map(c => c.id);
    setSelectedCourseIds(assignedIds);
    setAssignCoursesModalOpen(true);
  };

  const handleAssignCourses = async () => {
    setSaving(true);
    try {
      // Update teacher's course assignments
      await userAPI.assignTeacherCourses(selectedTeacher.id, selectedCourseIds);
      toast.success(language === 'fr' ? 'Cours assignés avec succès!' : 'Courses assigned successfully!');
      setAssignCoursesModalOpen(false);
      setSelectedTeacher(null);
      setSelectedCourseIds([]);
      loadData();
    } catch (error) {
      console.error('Error assigning courses:', error);
      toast.error(language === 'fr' ? 'Erreur d\'assignation' : 'Assignment error');
    } finally {
      setSaving(false);
    }
  };

  const toggleCourseSelection = (courseId) => {
    setSelectedCourseIds(prev => 
      prev.includes(courseId) 
        ? prev.filter(id => id !== courseId)
        : [...prev, courseId]
    );
  };

  const filteredTeachers = teachers.filter(t => 
    t.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getAssignedCoursesCount = (teacher) => {
    return courses.filter(c => 
      c.teacher_ids?.includes(teacher.id) || c.teacher_id === teacher.id
    ).length;
  };

  return (
    <div className="space-y-6" data-testid="teacher-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-outfit font-bold text-slate-900">
            {language === 'fr' ? 'Gestion des Enseignants' : 'Teacher Management'}
          </h1>
          <p className="text-slate-500">
            {language === 'fr' 
              ? `${filteredTeachers.length} enseignant(s) enregistré(s)`
              : `${filteredTeachers.length} teacher(s) registered`
            }
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => setImportOpen(true)}
            data-testid="import-teachers-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            {language === 'fr' ? 'Importer CSV' : 'Import CSV'}
          </Button>
          <Button 
            className="bg-emerald-700 hover:bg-emerald-800" 
            onClick={() => setCreateModalOpen(true)}
            data-testid="add-teacher-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {language === 'fr' ? 'Ajouter un Enseignant' : 'Add Teacher'}
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input 
              placeholder={language === 'fr' ? 'Rechercher par nom ou email...' : 'Search by name or email...'}
              className="pl-9" 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Teachers List */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="font-outfit flex items-center gap-2">
            <UserCog className="w-5 h-5 text-amber-600" />
            {language === 'fr' ? 'Liste des Enseignants' : 'Teacher List'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            </div>
          ) : filteredTeachers.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <UserCog className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>{language === 'fr' ? 'Aucun enseignant trouvé' : 'No teachers found'}</p>
              <Button 
                variant="outline" 
                className="mt-3"
                onClick={() => setCreateModalOpen(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Ajouter un enseignant' : 'Add a teacher'}
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">
                      {language === 'fr' ? 'Enseignant' : 'Teacher'}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Email</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">
                      {language === 'fr' ? 'Spécialisation' : 'Specialization'}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">
                      {language === 'fr' ? 'Cours Assignés' : 'Assigned Courses'}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTeachers.map((teacher) => (
                    <tr key={teacher.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                            <UserCog className="w-5 h-5 text-amber-700" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">
                              {teacher.first_name} {teacher.last_name}
                            </p>
                            {teacher.phone && (
                              <p className="text-xs text-slate-500 flex items-center gap-1">
                                <Phone className="w-3 h-3" /> {teacher.phone}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm text-slate-600 flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {teacher.email}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm text-slate-600">
                          {teacher.specialization || '-'}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1"
                          onClick={() => openAssignCoursesModal(teacher)}
                        >
                          <BookOpen className="w-3 h-3" />
                          {getAssignedCoursesCount(teacher)} {language === 'fr' ? 'cours' : 'courses'}
                          <Link2 className="w-3 h-3" />
                        </Button>
                      </td>
                      <td className="py-4 px-4">
                        <Badge className={teacher.is_active !== false
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-slate-100 text-slate-600'
                        }>
                          {teacher.is_active !== false 
                            ? (language === 'fr' ? 'Actif' : 'Active')
                            : (language === 'fr' ? 'Inactif' : 'Inactive')
                          }
                        </Badge>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditModal(teacher)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              {language === 'fr' ? 'Modifier' : 'Edit'}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openAssignCoursesModal(teacher)}>
                              <Link2 className="w-4 h-4 mr-2" />
                              {language === 'fr' ? 'Assigner des cours' : 'Assign Courses'}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggleActive(teacher)}>
                              {teacher.is_active !== false ? (
                                <>
                                  <UserX className="w-4 h-4 mr-2" />
                                  {language === 'fr' ? 'Désactiver' : 'Deactivate'}
                                </>
                              ) : (
                                <>
                                  <UserCheck className="w-4 h-4 mr-2" />
                                  {language === 'fr' ? 'Activer' : 'Activate'}
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedTeacher(teacher);
                                setResetPasswordOpen(true);
                              }}
                              data-testid={`reset-password-teacher-${teacher.id}`}
                            >
                              <KeyRound className="w-4 h-4 mr-2" />
                              {language === 'fr' ? 'Réinitialiser mot de passe' : 'Reset password'}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => {
                                setSelectedTeacher(teacher);
                                setDeleteModalOpen(true);
                              }}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              {language === 'fr' ? 'Supprimer' : 'Delete'}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {language === 'fr' ? 'Ajouter un Enseignant' : 'Add Teacher'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr' 
                ? 'Créez un compte enseignant pour votre école'
                : 'Create a teacher account for your school'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Prénom' : 'First Name'} *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => handleFormChange('first_name', e.target.value)}
                  placeholder="Amadou"
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Nom' : 'Last Name'} *</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => handleFormChange('last_name', e.target.value)}
                  placeholder="Ba"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
                placeholder="prof@ecole.sn"
              />
            </div>

            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Téléphone' : 'Phone'}</Label>
              <Input
                value={formData.phone}
                onChange={(e) => handleFormChange('phone', e.target.value)}
                placeholder="+221 77 000 0000"
              />
            </div>

            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Spécialisation' : 'Specialization'}</Label>
              <Input
                value={formData.specialization}
                onChange={(e) => handleFormChange('specialization', e.target.value)}
                placeholder={language === 'fr' ? 'Ex: Mathématiques, Français...' : 'Ex: Mathematics, French...'}
              />
            </div>

            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Mot de passe' : 'Password'} *</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => handleFormChange('password', e.target.value)}
                placeholder="••••••••"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateModalOpen(false); resetForm(); }}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleCreateTeacher}
              disabled={saving}
              className="bg-emerald-700 hover:bg-emerald-800"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {language === 'fr' ? 'Ajouter' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {language === 'fr' ? 'Modifier l\'Enseignant' : 'Edit Teacher'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Prénom' : 'First Name'} *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => handleFormChange('first_name', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Nom' : 'Last Name'} *</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => handleFormChange('last_name', e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Téléphone' : 'Phone'}</Label>
              <Input
                value={formData.phone}
                onChange={(e) => handleFormChange('phone', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Spécialisation' : 'Specialization'}</Label>
              <Input
                value={formData.specialization}
                onChange={(e) => handleFormChange('specialization', e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditModalOpen(false); setSelectedTeacher(null); resetForm(); }}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleEditTeacher}
              disabled={saving}
              className="bg-emerald-700 hover:bg-emerald-800"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {language === 'fr' ? 'Enregistrer' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Courses Modal */}
      <Dialog open={assignCoursesModalOpen} onOpenChange={setAssignCoursesModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {language === 'fr' 
                ? `Assigner des cours à ${selectedTeacher?.first_name} ${selectedTeacher?.last_name}`
                : `Assign courses to ${selectedTeacher?.first_name} ${selectedTeacher?.last_name}`
              }
            </DialogTitle>
            <DialogDescription>
              {language === 'fr' 
                ? 'Sélectionnez les cours que cet enseignant peut gérer'
                : 'Select the courses this teacher can manage'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 max-h-[400px] overflow-y-auto">
            {courses.length === 0 ? (
              <p className="text-center text-slate-500 py-4">
                {language === 'fr' ? 'Aucun cours disponible' : 'No courses available'}
              </p>
            ) : (
              <div className="space-y-2">
                {courses.map((course) => (
                  <div 
                    key={course.id}
                    className="flex items-center gap-3 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer"
                    onClick={() => toggleCourseSelection(course.id)}
                  >
                    <Checkbox 
                      checked={selectedCourseIds.includes(course.id)}
                      onCheckedChange={() => toggleCourseSelection(course.id)}
                    />
                    <BookOpen className="w-5 h-5 text-emerald-600" />
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{course.title_fr || course.title}</p>
                      <p className="text-xs text-slate-500">
                        {course.total_lessons || 0} {language === 'fr' ? 'leçons' : 'lessons'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setAssignCoursesModalOpen(false); setSelectedTeacher(null); setSelectedCourseIds([]); }}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleAssignCourses}
              disabled={saving}
              className="bg-emerald-700 hover:bg-emerald-800"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {language === 'fr' ? 'Assigner' : 'Assign'} ({selectedCourseIds.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">
              {language === 'fr' ? 'Supprimer l\'Enseignant' : 'Delete Teacher'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr' 
                ? `Êtes-vous sûr de vouloir supprimer ${selectedTeacher?.first_name} ${selectedTeacher?.last_name}?`
                : `Are you sure you want to delete ${selectedTeacher?.first_name} ${selectedTeacher?.last_name}?`
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleDeleteTeacher}
              disabled={saving}
              variant="destructive"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {language === 'fr' ? 'Supprimer' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Reset Modal */}
      <PasswordResetDialog
        open={resetPasswordOpen}
        onOpenChange={setResetPasswordOpen}
        user={selectedTeacher}
        userId={selectedTeacher?.user_id}
      />

      {/* Bulk CSV Import Modal */}
      <StudentImportDialog
        mode="teacher"
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={loadData}
      />
    </div>
  );
}
